import { Circle, MapPin, Pencil, Square } from "lucide-react";
import { useEffect } from "react";
import { ToggleButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";

import { Tooltip, TooltipTrigger } from "@/components/tooltip";

import type { EditorTool } from "./editorTools";

const TOOLS = [
  { icon: Square, key: "b", label: "Box", tool: "box" },
  { icon: Pencil, key: "p", label: "Pencil", tool: "pencil" },
  { icon: MapPin, key: "t", label: "Pin", tool: "text" },
  { icon: Circle, key: "o", label: "Ellipse", tool: "ellipse" },
] as const;

type EditorToolRailProps = {
  isDisabled: boolean;
  onChangeTool: (tool: EditorTool) => void;
  onRedo: () => void;
  onUndo: () => void;
  tool: EditorTool;
};

function isTyping(target: EventTarget | null) {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ["INPUT", "TEXTAREA"].includes(target.tagName))
  );
}

export function EditorToolRail({
  isDisabled,
  onChangeTool,
  onRedo,
  onUndo,
  tool,
}: EditorToolRailProps) {
  useEffect(() => {
    if (isDisabled) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (isTyping(event.target)) return;
      const key = event.key.toLowerCase();

      if ((event.ctrlKey || event.metaKey) && key === "z") {
        event.preventDefault();
        (event.shiftKey ? onRedo : onUndo)();

        return;
      }
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      const match = TOOLS.find((candidate) => candidate.key === key);

      if (match) onChangeTool(match.tool);
    };

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  });

  return (
    <div
      aria-label="Editing tools"
      className="flex w-[77px] shrink-0 flex-col items-center gap-2.5 border-r border-border-secondary py-[18px]"
      role="toolbar"
    >
      {TOOLS.map(({ icon: Icon, key, label, tool: toolId }) => (
        <TooltipTrigger delay={300} key={toolId}>
          <ToggleButton
            aria-label={`${label} (${key.toUpperCase()})`}
            className={twMerge(
              "flex size-12 cursor-pointer items-center justify-center rounded-xl border border-border-primary bg-bg-primary text-fg-secondary outline-hidden hover:bg-bg-primary-hover focus-visible:ring-2 focus-visible:ring-border-brand disabled:cursor-not-allowed disabled:text-fg-disabled",
              tool === toolId &&
                "border-transparent bg-button-primary-bg text-button-primary-fg hover:bg-button-primary-bg-hover",
            )}
            isDisabled={isDisabled}
            isSelected={tool === toolId}
            onChange={() => onChangeTool(toolId)}
          >
            <Icon size={22} />
          </ToggleButton>
          <Tooltip>
            {label} · {key.toUpperCase()}
          </Tooltip>
        </TooltipTrigger>
      ))}
    </div>
  );
}
